<script lang="ts">
  /**
   * Joining a room, as a screen of its own: the shell's `JoinTemplate` in its
   * `screen` frame (drawn in place under a back chevron; `App.svelte` owns
   * the history entry, which the room then takes over;
   * `docs/decisions/0054`). The code boxes, Paste and Join; a room that
   * exists opens the template's confirmation card, which asks the seat
   * question: the name (of a guest) and whether to play or to watch.
   *
   * ## The code
   *
   * Interlock's codes are four characters of a restricted alphabet, and this
   * screen hands the template the game's three steps (`net/room-code.ts`,
   * `net/share.ts`):
   *
   * - `extract`: a pasted join link of either form (`/join/CODE`, and the
   *   older `#/join/CODE`) leaves its code; anything else is the shell's own
   *   extraction (a link's last path segment, or the trimmed text);
   * - `normalise`: the look-alikes fold onto the alphabet (an `O` for a `Q`);
   * - `allowed`: what may be typed is the alphabet plus those look-alikes.
   *
   * The template runs the three on everything typed, pasted (its Paste
   * button too) or set, so the boxes always show the code that will be sent.
   * While the room is being looked up the template holds the boxes and Paste
   * (shell 1.3.0), so the code asked about cannot change under the question.
   * A Paste that brings nothing keeps the code that was there and says so in
   * Interlock's words (`pasteError`); the keys then go to the boxes.
   *
   * ## Arriving on a join link
   *
   * `prefillCode` is the code the page was opened on. The boxes are filled
   * in and the room is looked up at once, so the link lands on the
   * confirmation card: one press and you are in. An invitation gets one
   * look-up: `onInviteSpent` tells `App.svelte` to forget the code the moment
   * the look-up starts, so coming back to this screen later does not look it
   * up again.
   */
  import { onMount, untrack } from 'svelte';

  import { ChoiceTiles, JoinTemplate, extractCode } from '@artibens/app-shell';
  import type { ChoiceTileOption, JoinConfirm } from '@artibens/app-shell';
  import { ROOM_CODE_LENGTH, isRoomCode, normaliseRoomCode } from '@interlock/protocol';

  import { startAudioForGesture } from '../audio/bootstrap.js';
  import PlayAsField from '../nav/PlayAsField.svelte';
  import { findRoom } from '../net/connect.js';
  import { ROOM_CODE_TYPEABLE } from '../net/room-code.js';
  import { codeFromJoinLink } from '../net/share.js';
  import { account } from '../state/account.svelte.js';
  import { loadGuestName, saveGuestName } from '../state/guest-name-io.js';

  const {
    onBack,
    onJoin,
    error: joinError = '',
    prefillCode = '',
    onInviteSpent,
  }: {
    onBack: () => void;
    /** Enters the room. Settles when the join has been tried. */
    onJoin: (request: {
      roomId: string;
      code: string;
      name: string;
      role: 'player' | 'spectator';
    }) => Promise<void>;
    /** Why entering the room failed, from `App.svelte`. */
    error?: string;
    prefillCode?: string;
    /** Called once, when the invitation this screen was handed gets its one look-up. */
    onInviteSpent?: () => void;
  } = $props();

  type Role = 'player' | 'spectator';

  /** Said in the error line after a Paste that brought nothing (shell 1.3.0's `pasteError`). */
  const PASTE_ERROR = "Couldn't read the clipboard. Type the code instead.";

  const roleOptions: ChoiceTileOption<Role>[] = [
    { value: 'player', name: 'Player', note: 'Take the free seat', testid: 'join-role-player' },
    {
      value: 'spectator',
      name: 'Spectator',
      note: 'Watch the match',
      testid: 'join-role-spectator',
    },
  ];

  let name = $state(account.player?.username ?? loadGuestName());
  let code = $state(untrack(() => prefillCode));
  /** The invitation this screen arrived with, until its look-up has settled. */
  let invite = $state(untrack(() => prefillCode));
  let busy = $state(false);
  let error = $state('');
  let role = $state<Role>('player');
  /** The room the code named, while the confirmation card is up. */
  let found = $state<{ roomId: string; code: string } | null>(null);

  /** A join link of either form leaves its code; anything else is the shell's to read. */
  function extract(raw: string): string {
    return codeFromJoinLink(raw) ?? extractCode(raw);
  }

  /** Holds the template, for the code boxes' input (`pasteFailed`). */
  let root: HTMLDivElement | undefined = $state();

  /**
   * Paste brought nothing (the clipboard refused, empty, or no code in it):
   * the template keeps the code and says `PASTE_ERROR`, and the keys go to
   * the boxes, because the sentence asks for the code to be typed.
   */
  function pasteFailed(): void {
    root?.querySelector('input')?.focus();
  }

  const codeLooksRight = $derived(isRoomCode(code));
  const nameLooksRight = $derived(name.trim().length > 0);
  const shownError = $derived(error !== '' ? error : joinError);

  onMount(() => {
    if (invite !== '') void lookUp();
  });

  async function lookUp(): Promise<void> {
    if (!codeLooksRight || busy) return;
    const wanted = code;
    // Spent as the look-up begins, not when it ends, so leaving in the
    // middle of it cannot offer the same invitation a second time.
    if (invite !== '') onInviteSpent?.();
    busy = true;
    error = '';
    try {
      const roomId = await findRoom(wanted);
      if (roomId === null) {
        error = `No room with the code ${wanted}.`;
        return;
      }
      role = 'player';
      found = { roomId, code: wanted };
    } catch (cause) {
      error = cause instanceof Error ? cause.message : 'Could not reach the server.';
    } finally {
      busy = false;
      invite = '';
    }
  }

  async function enter(): Promise<void> {
    const room = found;
    if (room === null || !nameLooksRight || busy) return;
    // A gesture for the audio context: this tap is the one that enters.
    startAudioForGesture();
    saveGuestName(name);
    found = null;
    busy = true;
    try {
      await onJoin({ roomId: room.roomId, code: room.code, name: name.trim(), role });
    } finally {
      busy = false;
    }
  }

  const confirm = $derived<JoinConfirm | null>(
    found === null
      ? null
      : {
          title: `Join room ${found.code}`,
          details: roomDetails,
          role: seatQuestion,
          join: {
            label: role === 'spectator' ? 'Watch' : 'Join',
            disabled: !nameLooksRight,
            onclick: () => void enter(),
          },
          dismiss: { label: 'Not now', testid: 'join-not-now' },
          onclose: () => {
            found = null;
          },
          testid: 'join-confirm',
        },
  );
</script>

{#snippet roomDetails()}
  <p class="join-card-line">The room is open. Play in it, or watch the match.</p>
{/snippet}

<!-- The seat question: the moment a seat is taken is where a guest's name is
     asked. With no name to play under, the keyboard starts in the field,
     because the name is what is missing. -->
{#snippet seatQuestion()}
  <PlayAsField bind:name autofocus={!nameLooksRight} onenter={() => void enter()} />
  <ChoiceTiles
    label="Join as"
    value={role}
    options={roleOptions}
    onchange={(next) => {
      role = next;
    }}
    testid="join-role"
  />
{/snippet}

{#snippet inviteNotice()}
  {#if invite !== ''}
    <p class="join-invite" data-testid="invite-notice">
      You were invited to room <strong class="join-code" data-ui-font="digits">{invite}</strong>.
      Looking it up…
    </p>
  {/if}
{/snippet}

<!-- `display: contents`: a handle on the template, not a box of its own. -->
<div class="join-screen" bind:this={root}>
  <JoinTemplate
    frame="screen"
    title="Join a game"
    onclose={onBack}
    prompt={`Type the ${String(ROOM_CODE_LENGTH)}-letter code your friend sent you.`}
    bind:code
    codeLength={ROOM_CODE_LENGTH}
    codeLabel={`Room code, ${String(ROOM_CODE_LENGTH)} characters`}
    allowed={ROOM_CODE_TYPEABLE}
    normalise={normaliseRoomCode}
    {extract}
    onlookup={() => void lookUp()}
    lookup={{ label: 'Join', busy, testid: 'join-submit' }}
    error={shownError === '' ? null : shownError}
    pasteError={PASTE_ERROR}
    onpastefail={pasteFailed}
    hint="A link from a friend opens this for you, code and all."
    extra={inviteNotice}
    {confirm}
    autofocus={untrack(() => prefillCode === '')}
    testid="join-panel"
    testids={{ code: 'join-code', paste: 'join-paste', error: 'join-error' }}
  />
</div>

<style>
  .join-screen {
    display: contents;
  }

  .join-invite {
    margin: 0;
    text-align: center;
    font: var(--ui-text-label);
    font-weight: 400;
    line-height: 1.45;
    color: var(--ui-ink-2);
  }

  /* The found room, under the card's title: a detail line, as the house
     card has it. */
  .join-card-line {
    margin: 0;
    font: var(--ui-text-label);
    line-height: 1.45;
    color: var(--ui-ink-2);
  }

  .join-code {
    font-family: var(--ui-font-digits);
    font-weight: 700;
    color: var(--ui-ink);
  }
</style>
